import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// ============================================================
// GlobalTracking.tsx
// Handles ALL client-side tracking across every page:
//  1. Page-view events on SPA route changes (GTM dataLayer + gtag)
//  2. Conversion events: phone clicks, WhatsApp clicks, form submits
//  3. Custom per-page conversions (configurable via dashboard)
//
// NOTE: Google Tag injection (gtag.js, GTM snippet, head/body scripts)
//       is handled SERVER-SIDE by index.php — not here.
//       This component only fires runtime EVENTS to already-loaded tags.
// ============================================================

interface TrackingSettings {
  gtag_id?: string;
  gtm_id?: string;
  conversion_label?: string;
  conversion_scripts?: string;
  page_conversion_scripts?: string;
  conversion_events?: Record<string, { send_to: string; value?: number; currency?: string }>;
}

// Default Google Ads tag ID (same as in index.php)
const DEFAULT_TAG_ID = 'AW-10801018169';

export function GlobalTracking() {
  const location = useLocation();
  const [settings, setSettings] = useState<TrackingSettings>({});
  const settingsRef = useRef<TrackingSettings>({});

  // Keep ref in sync so event handlers always have latest settings
  // without re-registering listeners on every settings change
  settingsRef.current = settings;

  // ── 1. Load settings from API (once on mount) ─────────────
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch('/api/settings');
        const ct = res.headers.get('content-type') || '';
        if (res.ok && ct.includes('application/json')) {
          const data = await res.json();
          if (data && typeof data === 'object') {
            const parsed: TrackingSettings = {
              gtag_id:          data.gtag_id          || '',
              gtm_id:           data.gtm_id           || '',
              conversion_label: data.conversion_label || '',
              conversion_scripts: data.conversion_scripts || '',
              page_conversion_scripts: data.page_conversion_scripts || '',
            };
            // Parse per-page conversion map
            if (data.page_conversion_scripts) {
              try {
                parsed.conversion_events = JSON.parse(data.page_conversion_scripts);
              } catch (_) {}
            }
            setSettings(parsed);
            // Cache for offline fallback
            localStorage.setItem('_tracking_cache', JSON.stringify(parsed));
            return;
          }
        }
      } catch (_) {}

      // Fallback: localStorage cache
      try {
        const cached = localStorage.getItem('_tracking_cache')
          || localStorage.getItem('local_tracking_settings');
        if (cached) setSettings(JSON.parse(cached));
      } catch (_) {}
    };

    loadSettings();
  }, []);

  // ── 2. Page-view tracking on every SPA route change ───────
  // Fires for EVERY page: /, /leistungen, /standorte/*, /seo-panel etc.
  useEffect(() => {
    const timer = setTimeout(() => {
      const fullPath = location.pathname + location.search;
      const s = settingsRef.current;

      // ── GTM dataLayer push ────────────────────────────────
      if (window.dataLayer) {
        window.dataLayer.push({
          event:     'page_view',
          page_path: fullPath,
          page_title: document.title,
        });
      }

      // ── gtag page_view ────────────────────────────────────
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path:  fullPath,
          page_title: document.title,
        });

        // Per-page conversion (configured in dashboard JSON field)
        if (s.conversion_events?.[location.pathname]) {
          const conv = s.conversion_events[location.pathname];
          window.gtag('event', 'conversion', {
            send_to:  conv.send_to,
            value:    conv.value    ?? 1.0,
            currency: conv.currency ?? 'EUR',
          });
        }
      }
    }, 150); // slight delay so React has finished rendering

    return () => clearTimeout(timer);
  }, [location]);

  // ── 3. Global click & form conversion listeners ────────────
  // Registered ONCE and uses ref to always access latest settings.
  // Intercepts: tel: links, WhatsApp links, form submits
  useEffect(() => {

    const fireConversion = (type: string, page: string, value: number, s: TrackingSettings) => {
      const adsTarget = s.conversion_label || s.gtag_id || DEFAULT_TAG_ID;

      // GTM
      if (window.dataLayer) {
        window.dataLayer.push({
          event:            'conversion',
          conversion_type:  type,
          event_category:   'lead',
          event_label:      page,
          value,
          currency: 'EUR',
        });
      }

      // gtag: GA4 lead event + Google Ads conversion
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: type === 'form_submission' ? 'form' : 'contact',
          event_label:    page,
          value,
          currency: 'EUR',
        });

        // Google Ads conversion (send_to your conversion label/tag)
        window.gtag('event', 'conversion', {
          send_to:  adsTarget,
          value,
          currency: 'EUR',
        });
      }

      // Custom JS from dashboard (Conversion Scripts field)
      if (s.conversion_scripts) {
        try {
          const fn = new Function('conversionType', 'page', 'value', s.conversion_scripts);
          fn(type, page, value);
        } catch (err) {
          console.error('[Tracking] conversion_scripts error:', err);
        }
      }
    };

    const handleClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement)?.closest('a');
      if (!el) return;

      const href = el.getAttribute('href') || '';
      const page = window.location.pathname;
      const s    = settingsRef.current;

      if (href.startsWith('tel:')) {
        console.log('[Tracking] Phone click →', href, 'on', page);
        fireConversion('phone_call_click', page, 10.0, s);
      } else if (href.includes('wa.me') || href.includes('whatsapp')) {
        console.log('[Tracking] WhatsApp click on', page);
        fireConversion('whatsapp_click', page, 10.0, s);
      }
    };

    const handleSubmit = (e: SubmitEvent) => {
      const page = window.location.pathname;
      console.log('[Tracking] Form submit on', page);
      fireConversion('form_submission', page, 15.0, settingsRef.current);
    };

    document.addEventListener('click',  handleClick  as EventListener);
    document.addEventListener('submit', handleSubmit as EventListener);

    return () => {
      document.removeEventListener('click',  handleClick  as EventListener);
      document.removeEventListener('submit', handleSubmit as EventListener);
    };
  }, []); // Empty deps — registered once, uses ref for live settings

  // This component renders nothing — it only runs side effects
  return null;
}

// TypeScript global declarations
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (...args: unknown[]) => void;
  }
}
